import { axiosInstance } from "../../../api/axiosInstance";
import { appCache } from "../../../cache/appCache";

const PROFILE_URL = "/admin/school-profile";
const LOGO_URL = "/admin/school-profile/logo";

// Mirrors the backend's SchoolProfileDto
export interface SchoolProfile {
  name: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  pincode: string;
  phone?: string | null;
  email?: string | null;
  hasLogo?: boolean;
}

export const EMPTY_PROFILE: SchoolProfile = { name: "", addressLine1: "", addressLine2: "", city: "", state: "", pincode: "", phone: "", email: "" };

// The saved profile, or null before one is saved (204). Cached (appCache bucket "schoolProfile").
export const fetchSchoolProfile = (): Promise<SchoolProfile | null> =>
  appCache.getOrFetch("schoolProfile", () => axiosInstance.get<SchoolProfile>(PROFILE_URL).then((response) => (response.status === 204 ? null : response.data)));

export const saveSchoolProfile = async (profile: SchoolProfile): Promise<SchoolProfile> => {
  const { hasLogo, ...body } = profile;
  const response = await axiosInstance.put<SchoolProfile>(PROFILE_URL, body);
  appCache.set("schoolProfile", response.data);
  return response.data;
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

// The logo as a data URL, or null when there is none (404). Cached (appCache bucket "schoolLogo").
export const fetchSchoolLogo = (): Promise<string | null> =>
  appCache.getOrFetch("schoolLogo", async () => {
    try {
      const response = await axiosInstance.get(LOGO_URL, { responseType: "blob" });
      return await blobToDataUrl(response.data);
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  });

export const uploadSchoolLogo = async (file: File): Promise<SchoolProfile> => {
  const form = new FormData();
  form.append("file", file);
  const response = await axiosInstance.put<SchoolProfile>(LOGO_URL, form, { headers: { "Content-Type": "multipart/form-data" } });
  appCache.set("schoolProfile", response.data);
  appCache.set("schoolLogo", await blobToDataUrl(file));
  return response.data;
};

export const removeSchoolLogo = async (): Promise<SchoolProfile> => {
  const response = await axiosInstance.delete<SchoolProfile>(LOGO_URL);
  appCache.set("schoolProfile", response.data);
  appCache.set("schoolLogo", null);
  return response.data;
};
